import { CdkDrag, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  InputSignal,
  isDevMode,
  linkedSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  TemplateRef,
  untracked,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DeightonStatusBannerComponent } from '@deighton/shared-ui/layout';
import type { GridAction } from '../contracts/grid-action.interface';
import type { GridStatePersistence } from '../contracts/grid-state-persistence.interface';
import { saveGridLayout } from '../grid-layout-persistence';
import { GRID_STATE_PERSISTENCE } from '../in-memory-grid-state-persistence';
import type { GroupingCapKind, GroupingCapWarning } from '../contracts/grouping-cap-warning.interface';
import { GRID_FEATURES, type GridFeatures } from '../contracts/grid-features.interface';
import type { GridScopeFilterMode } from '../contracts/data-grid-state.interface';
import type { GridRow } from '../contracts/grid-row.type';
import type {
  GridActionTriggeredEvent,
  GridRowKeyFn,
  GridRowSelectionMode,
  GridSelectionDescriptor
} from '../contracts/grid-row-selection.interface';
import { groupingCapMessageKey } from '../orchestration';
import { ColumnFilterMenuComponent } from './column-filter-menu/column-filter-menu.component';
import { DataGridColumnsPopoverComponent } from './columns-popover/data-grid-columns-popover.component';
import type { ColumnFilter, ColumnFilterMap } from './column-filter.types';
import {
  buildActiveFilterChipEntries,
  isFilterActive,
  serializeFilters,
  type ActiveFilterChipEntry
} from './column-filter.utils';
import {
  applyColumnOrder as orderColumns,
  buildColumnSections,
  collectHiddenByDefaultFields,
  filterHiddenColumns as hideColumns
} from './data-grid-column-layout.utils';
import {
  buildNestedGroups as partitionNestedGroups,
  collectExpandedUnloadedLeaves as collectUnloadedLeaves,
  collectLeafIds as collectExpandedLeafIds,
  collectLoadsForExpandedLeaves,
  isGroupExpanded as groupIsExpanded,
  nextSortState as cycleSortState,
  resolveLeafElementType,
  resolveRowsForPath,
  rowsForSortState,
  type DataGridGroupBuildContext
} from './data-grid-grouping.utils';
import {
  DATA_GRID_FLAT_SORT_PATH,
  DATA_GRID_FLAT_THEAD_ID,
  DATA_GRID_ROW_HEIGHT
} from './data-grid-virtual-scroll.utils';
import {
  DataGridColumnDragData,
  DataGridColumnSection,
  DataGridGroupBucketViewState,
  DataGridGroupSortState,
  DataGridColumn,
  DataGridState,
  DataGridTheme,
  GroupedColumnOverride,
  DataGridGroupNode,
  SortDirection
} from './data-grid.types';
import { applyGroupedColumnOverrides } from './element-grid-grouped-columns.utils';
import { DataGridGroupBarComponent } from './group-bar/data-grid-group-bar.component';
import { DataGridGroupTreeComponent } from './group-tree/data-grid-group-tree.component';
import type { DataGridGroupTreeViewModel } from './group-tree/data-grid-group-tree.view-model';
import { DataGridSelectionStore } from './selection/data-grid-selection.store';
import {
  areSelectionDescriptorsEqual,
  canResolveRowKey,
  DATA_GRID_SELECTION_COLUMN_WIDTH,
  isGridActionEnabled,
  partitionGridActions,
  resolveRowKey
} from './selection/data-grid-selection.utils';
import {
  DataGridTableComponent,
  type DataGridColumnReorderEvent,
  type DataGridFilterMenuOpenEvent,
  type DataGridHeaderSortEvent
} from './table/data-grid-table.component';

/** Number of placeholder columns rendered in the skeleton loader. */
const SKELETON_COLUMN_COUNT: number = 8;

/** Number of placeholder rows rendered in the skeleton loader. */
const SKELETON_ROW_COUNT: number = 12;

/**
 * Element grid with drag-and-drop multi-level grouping and per-type column headers.
 * Shared columns align across all groups; type-specific columns appear after a visual separator
 * when the differentiator field is part of the grouping chain.
 */
@Component({
  selector: 'd-data-grid',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    TranslocoPipe,
    MatProgressBarModule,
    ColumnFilterMenuComponent,
    DataGridColumnsPopoverComponent,
    DeightonStatusBannerComponent,
    DataGridGroupBarComponent,
    DataGridGroupTreeComponent,
    DataGridTableComponent
  ],
  providers: [DataGridSelectionStore],
  templateUrl: './d-data-grid.component.html',
  styleUrl: './d-data-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.dg-resizing]': 'isResizing()',
    '(keydown)': 'onHostKeydown($event)',
    '[style.--dg-surface]': 'theme().surface ?? null',
    '[style.--dg-surface-container]': 'theme().surfaceContainer ?? null',
    '[style.--dg-surface-variant]': 'theme().surfaceVariant ?? null',
    '[style.--dg-on-surface]': 'theme().onSurface ?? null',
    '[style.--dg-on-surface-variant]': 'theme().onSurfaceVariant ?? null',
    '[style.--dg-primary]': 'theme().primary ?? null',
    '[style.--dg-border-color]': 'theme().borderColor ?? null',
    '[style.--dg-row-stripe]': 'theme().rowStripe ?? null',
    '[style.--dg-row-hover]': 'theme().rowHover ?? null',
    '[style.--dg-header-type-specific-bg]': 'theme().headerDiscriminatorBg ?? null',
    '[style.--dg-header-hover-bg]': 'theme().headerHoverBg ?? null',
    '[style.--dg-chip-bg]': 'theme().chipBg ?? null',
    '[style.--dg-chip-bg-active]': 'theme().chipBgActive ?? null',
    '[style.--dg-chip-color]': 'theme().chipColor ?? null',
    '[style.--dg-chip-border]': 'theme().chipBorder ?? null',
    '[style.--dg-danger]': 'theme().danger ?? null',
    '[style.--dg-checkbox-outline]': 'theme().checkboxOutline ?? null'
  }
})
export class DeightonDataGridComponent {
  /** Per-instance feature overrides merged over {@link GRID_FEATURES}. */
  public features: InputSignal<Partial<GridFeatures>> = input<Partial<GridFeatures>>({});

  /** Custom template for the leading row-action cell; overrides the default fly-to button. */
  public rowActionTemplate: InputSignal<TemplateRef<{ $implicit: Record<string, unknown> }> | null> =
    input<TemplateRef<{ $implicit: Record<string, unknown> }> | null>(null);

  /** Width of the row-action column in pixels when visible. */
  public rowActionWidth: InputSignal<number> = input<number>(36);

  /** Consumer-defined toolbar actions gated by the current selection. */
  public gridActions: InputSignal<ReadonlyArray<GridAction>> = input<ReadonlyArray<GridAction>>(
    new Array<GridAction>()
  );

  /** Row selection mode. Ignored when `rowSelection` is off or selection UI is hidden. */
  public rowSelectionMode: InputSignal<GridRowSelectionMode> = input<GridRowSelectionMode>('multiple');

  /**
   * When true, renders selection checkboxes and enables click-to-select.
   * Defaults off so inventory hosts look unchanged until the panel toggle is used.
   */
  public selectionUiEnabled: InputSignal<boolean> = input<boolean>(false);

  /** Field used as the row identity when `rowKeyFn` is not set. */
  public rowKeyField: InputSignal<string> = input<string>('');

  /** Optional function that returns a stable row identity. */
  public rowKeyFn: InputSignal<GridRowKeyFn | null> = input<GridRowKeyFn | null>(null);

  /**
   * Scope filter mode forwarded from the panel so selection clears when it changes.
   * Unused for rendering.
   */
  public scopeFilterMode: InputSignal<GridScopeFilterMode | null> = input<GridScopeFilterMode | null>(null);

  /** Theme configuration. Falls back to --d-* design tokens when not provided. */
  public theme: InputSignal<DataGridTheme> = input<DataGridTheme>({});

  /** Optional override for the empty-state message. */
  public emptyMessage: InputSignal<string | null> = input<string | null>(null);

  /** Flat array of element records to display. */
  public data: InputSignal<Array<Record<string, unknown>>> = input<Array<Record<string, unknown>>>(
    new Array<Record<string, unknown>>()
  );

  /** Total count of elements matching the current server-side filters. */
  public totalCount: InputSignal<number> = input<number>(0);

  /** Column definitions shared across all element types. */
  public sharedColumns: InputSignal<Array<DataGridColumn>> = input<Array<DataGridColumn>>(
    new Array<DataGridColumn>()
  );

  /** Column definitions keyed by element type.  */
  public discriminatorColumns: InputSignal<Record<string, Array<DataGridColumn>>> = input<
    Record<string, Array<DataGridColumn>>
  >({});

  /**
   * Field used to group and colour rows by type.
   * Empty when the host has no type discriminator; inventory binds `INVENTORY_DISCRIMINATOR_FIELD`.
   */
  public discriminatorField: InputSignal<string> = input<string>('');

  /** Color map for element types. */
  public discriminatorColors: InputSignal<Record<string, string>> = input<Record<string, string>>({});

  /** Maps element type slug to its human-friendly display name. */
  public discriminatorLabels: InputSignal<Record<string, string>> = input<Record<string, string>>({});

  /**
   * Per-element-type grouped-leaf header overrides for shared columns.
   * Applied only when the differentiator is in the group chain.
   */
  public groupedColumnOverrides: InputSignal<
    ReadonlyMap<string, ReadonlyMap<string, GroupedColumnOverride>>
  > = input<ReadonlyMap<string, ReadonlyMap<string, GroupedColumnOverride>>>(
    new Map<string, ReadonlyMap<string, GroupedColumnOverride>>()
  );

  /** Column fields hidden by the user via the Columns popover. Parent-driven when bound. */
  public hiddenColumns: InputSignal<Set<string>> = input<Set<string>>(new Set<string>());

  /** Persisted shared column order (field names). Empty = use API default. */
  public columnOrder: InputSignal<Array<string>> = input<Array<string>>(new Array<string>());

  /** Persisted type-specific column order, keyed by element type. */
  public discriminatorColumnOrder: InputSignal<Record<string, Array<string>>> = input<
    Record<string, Array<string>>
  >({});

  /** Initial group-by fields provided by the parent (e.g., from persisted state). */
  public initialGroupByFields: InputSignal<Array<string>> = input<Array<string>>(new Array<string>());

  /** Persisted user-defined column widths, keyed by field name. */
  public columnWidths: InputSignal<Record<string, number>> = input<Record<string, number>>({});

  /** Key of the element currently hovered on the map. */
  public hoveredRowKey: InputSignal<number | null> = input<number | null>(null);

  /** Whether a data fetch is currently in progress. */
  public loading: InputSignal<boolean> = input<boolean>(false);

  /** Whether the group-summary phase is in progress. */
  public summaryLoading: InputSignal<boolean> = input<boolean>(false);

  /** Whether more primary group header pages can be loaded. */
  public hasMoreGroupHeaders: InputSignal<boolean> = input<boolean>(false);

  /** Whether more pages of data are available from the server. */
  public hasMore: InputSignal<boolean> = input<boolean>(false);

  /** Whether a pagination request is currently in flight. */
  public loadingMore: InputSignal<boolean> = input<boolean>(false);

  /** Per-bucket loading / hasNextPage flags keyed by composite group path. */
  public groupBucketStates: InputSignal<Record<string, DataGridGroupBucketViewState>> = input<
    Record<string, DataGridGroupBucketViewState>
  >({});

  /** Server-provided totalCount per element type. */
  public typeTotalCounts: InputSignal<Record<string, number>> = input<Record<string, number>>({});

  /** Server-provided totalCount per sub-group. */
  public subGroupCounts: InputSignal<Record<string, number>> = input<Record<string, number>>({});

  /** The outermost group field used for per-group parallel pagination. */
  public primaryGroupField: InputSignal<string | null> = input<string | null>(null);

  /** Group fields detected as high-cardinality. */
  public highCardinalityFields: InputSignal<Set<string>> = input<Set<string>>(new Set<string>());

  /**
   * camelCase field names that the UI allows grouping by.
   * Columns not in this list cannot be dragged to the group bar or toggled for grouping.
   * Sourced from SYSTEM `preferences.element_grid.groupableFields`.
   */
  public groupableFields: InputSignal<Array<string>> = input<Array<string>>(new Array<string>());

  /** Active column filters. */
  public columnFilters: InputSignal<ColumnFilterMap> = input<ColumnFilterMap>(
    new Map<string, ColumnFilter>()
  );

  /** Whether the info banner has been dismissed by the user. */
  public infoBannerDismissed: InputSignal<boolean> = input<boolean>(false);

  /** Warnings when grouped loading hit orchestration caps. */
  public groupingCapWarnings: InputSignal<Array<GroupingCapWarning>> = input<Array<GroupingCapWarning>>(
    new Array<GroupingCapWarning>()
  );

  /** Emits the current grid state whenever grouping changes (for persistence). */
  public gridStateChange: OutputEmitterRef<DataGridState> = output<DataGridState>();

  /** Emits when the user shows or hides columns from the Columns popover. */
  public hiddenColumnsChange: OutputEmitterRef<Set<string>> = output<Set<string>>();

  /** Emits when the user reorders shared columns. */
  public columnOrderChange: OutputEmitterRef<Array<string>> = output<Array<string>>();

  /** Emits when the user reorders type-specific columns within a leaf group. */
  public discriminatorColumnOrderChange: OutputEmitterRef<{ elementType: string; order: Array<string> }> =
    output<{ elementType: string; order: Array<string> }>();

  /** Emits when the user finishes resizing a column. */
  public columnWidthChange: OutputEmitterRef<{ field: string; width: number }> = output<{
    field: string;
    width: number;
  }>();

  /** Emits when the user changes sort in flat view, for server-side sort integration. */
  public sortChange: OutputEmitterRef<{ field: string; direction: SortDirection } | null> = output<{
    field: string;
    direction: SortDirection;
  } | null>();

  /** Emits when the user clicks the fly-to button on a row. */
  public rowAction: OutputEmitterRef<Record<string, unknown>> = output<Record<string, unknown>>();

  /** Emits the element key when the user hovers a grid row, or null on mouseleave. */
  public rowHover: OutputEmitterRef<number | null> = output<number | null>();

  /** Emits when the user scrolls near the end of loaded data and more pages exist. */
  public loadMore: OutputEmitterRef<string | null> = output<string | null>();

  /** Emits when the user requests the next page of primary group headers. */
  public loadMoreGroupHeaders: OutputEmitterRef<void> = output<void>();

  /** Emits when the user changes a column filter. */
  public filterChange: OutputEmitterRef<{ field: string; filter: ColumnFilter | null }> = output<{
    field: string;
    filter: ColumnFilter | null;
  }>();

  /** Emits when the user dismisses or restores the info banner. */
  public infoBannerDismissedChange: OutputEmitterRef<boolean> = output<boolean>();

  /** Emits whenever the row selection changes. */
  public selectionChange: OutputEmitterRef<GridSelectionDescriptor> = output<GridSelectionDescriptor>();

  /** Emits when the user triggers a declarative grid action. */
  public gridActionTriggered: OutputEmitterRef<GridActionTriggeredEvent> = output<GridActionTriggeredEvent>();

  /** Injected feature preset merged with per-instance overrides. */
  private readonly injectedFeatures: GridFeatures = inject(GRID_FEATURES);

  /** Layout persistence port shared with the panel and inventory store. */
  private readonly persistence: GridStatePersistence = inject(GRID_STATE_PERSISTENCE);

  /** Tears down one-shot persistence subscriptions. */
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /** Per-grid selection store shared with recursive tables. */
  private readonly selectionStore: DataGridSelectionStore = inject(DataGridSelectionStore);

  /** Resolves translated operator labels for filter chip summaries. */
  private readonly transloco: TranslocoService = inject(TranslocoService);

  /** Session-only dismiss flags for grouping-cap warning banners. */
  private readonly dismissedGroupingCapKinds: WritableSignal<Set<GroupingCapKind>> = signal<
    Set<GroupingCapKind>
  >(new Set<GroupingCapKind>());

  /** Last grouping-cap warning set used to reset dismiss flags only when warnings change. */
  private lastGroupingCapWarningsFingerprint: string = '';

  /** The column definition for the currently open filter menu, or null if closed. */
  protected openFilterColumn: WritableSignal<DataGridColumn | null> = signal<DataGridColumn | null>(null);

  /** The anchor <th> element for positioning the filter menu popup. */
  protected filterMenuAnchor: WritableSignal<HTMLElement | null> = signal<HTMLElement | null>(null);

  /** Depth-0 group paths the user has explicitly collapsed. */
  protected collapsedGroups: WritableSignal<Set<string>> = signal<Set<string>>(new Set<string>());

  /** Depth ≥ 1 group paths the user has explicitly expanded. */
  protected expandedGroups: WritableSignal<Set<string>> = signal<Set<string>>(new Set<string>());

  /** Per-group sort state keyed by composite path. */
  protected sortStates: WritableSignal<Record<string, DataGridGroupSortState | null>> = signal<
    Record<string, DataGridGroupSortState | null>
  >({});

  /** Fields currently used for grouping, in order. */
  protected groupByFields: WritableSignal<Array<string>> = signal<Array<string>>(new Array<string>());

  /** Whether a column resize drag is currently in progress. */
  protected isResizing: WritableSignal<boolean> = signal<boolean>(false);

  /** Flat sort state for ungrouped view. */
  protected flatSortState: WritableSignal<DataGridGroupSortState | null> =
    signal<DataGridGroupSortState | null>(null);

  /**
   * Hidden fields used for rendering. Tracks the parent input, persistence, and local popover edits
   * so standalone hosts do not need to bind `hiddenColumns`.
   */
  protected resolvedHiddenColumns: WritableSignal<Set<string>> = linkedSignal(
    (): Set<string> => new Set<string>(this.hiddenColumns())
  );

  /** Shared column order used for rendering. Tracks the parent input and persistence. */
  protected resolvedColumnOrder: WritableSignal<Array<string>> = linkedSignal((): Array<string> => [
    ...this.columnOrder()
  ]);

  /** Type-specific column order used for rendering. Tracks the parent input and persistence. */
  protected resolvedDiscriminatorColumnOrder: WritableSignal<Record<string, Array<string>>> = linkedSignal(
    (): Record<string, Array<string>> => ({ ...this.discriminatorColumnOrder() })
  );

  /** Column widths used for rendering. Tracks the parent input and persistence. */
  protected resolvedColumnWidths: WritableSignal<Record<string, number>> = linkedSignal(
    (): Record<string, number> => ({ ...this.columnWidths() })
  );

  /** Resolved feature flags for template gating. */
  protected resolvedFeatures: Signal<GridFeatures> = computed((): GridFeatures => ({
    ...this.injectedFeatures,
    ...this.features()
  }));

  /** Zero when no row-action column renders. */
  protected actionColumnWidth: Signal<number> = computed((): number => {
    if (this.rowActionTemplate() !== null || this.resolvedFeatures().rowAction) {
      return this.rowActionWidth();
    }
    return 0;
  });

  /** Whether the default fly-to action button should render (inventory preset). */
  protected showDefaultRowAction: Signal<boolean> = computed(
    (): boolean => this.resolvedFeatures().rowAction && this.rowActionTemplate() === null
  );

  /** Whether the first loaded row can resolve a selection key. */
  private readonly rowKeyResolvable: Signal<boolean> = computed((): boolean =>
    canResolveRowKey(this.data(), this.rowKeyFn(), this.rowKeyField())
  );

  /** Selection checkbox column width, or 0 when selection is off. */
  protected selectionColumnWidth: Signal<number> = computed((): number => {
    if (!this.resolvedFeatures().rowSelection || !this.selectionUiEnabled() || !this.rowKeyResolvable()) {
      return 0;
    }
    if (this.rowSelectionMode() === 'none') {
      return 0;
    }
    return DATA_GRID_SELECTION_COLUMN_WIDTH;
  });

  /** Live selection descriptor from the store. */
  protected selectionDescriptor: Signal<GridSelectionDescriptor> = computed((): GridSelectionDescriptor =>
    this.selectionStore.descriptor()
  );

  /** Estimated selected count for the summary label. */
  protected selectionCount: Signal<number> = computed((): number => this.selectionStore.count());

  /** Whether the selection summary and action bar should render. */
  protected showSelectionBar: Signal<boolean> = computed((): boolean => this.selectionCount() > 0);

  /** Whether the "select all matching" affordance should render. */
  protected showSelectAllMatching: Signal<boolean> = computed((): boolean => {
    if (!this.showSelectionBar() || this.selectionStore.allMatching()) {
      return false;
    }
    return this.totalCount() > this.data().length;
  });

  /** Whether the grouped select-all checkbox should render beside the record count. */
  protected showGroupedSelectAll: Signal<boolean> = computed((): boolean => {
    return (
      this.selectionColumnWidth() > 0 &&
      this.rowSelectionMode() === 'multiple' &&
      this.hasGrouping() &&
      this.totalCount() > 0
    );
  });

  /** Whether the grouped select-all checkbox is fully checked. */
  protected groupedSelectAllChecked: Signal<boolean> = computed(
    (): boolean => this.selectionStore.globalSelectionState() === 'all'
  );

  /** Whether the grouped select-all checkbox is indeterminate. */
  protected groupedSelectAllIndeterminate: Signal<boolean> = computed(
    (): boolean => this.selectionStore.globalSelectionState() === 'some'
  );

  /** Inline action buttons (up to the overflow limit). */
  protected inlineGridActions: Signal<Array<GridAction>> = computed((): Array<GridAction> => {
    return partitionGridActions(this.gridActions(), this.selectionDescriptor()).inline;
  });

  /** Overflow-menu action buttons. */
  protected overflowGridActions: Signal<Array<GridAction>> = computed((): Array<GridAction> => {
    return partitionGridActions(this.gridActions(), this.selectionDescriptor()).overflow;
  });

  /** True while any server fetch is in flight. */
  protected isGridBusy: Signal<boolean> = computed((): boolean => this.loading() || this.summaryLoading());

  /** Grouping-cap warnings that have not been dismissed this session. */
  protected activeGroupingCapWarnings: Signal<Array<GroupingCapWarning>> = computed(
    (): Array<GroupingCapWarning> => {
      const dismissed: Set<GroupingCapKind> = this.dismissedGroupingCapKinds();
      return this.groupingCapWarnings().filter(
        (warning: GroupingCapWarning): boolean => !dismissed.has(warning.kind)
      );
    }
  );

  /** Whether any grouping-cap warning banner should render. */
  protected showGroupingCapBanner: Signal<boolean> = computed((): boolean => {
    return this.activeGroupingCapWarnings().length > 0;
  });

  /** Active filter entries for removable chips in the group bar. */
  protected activeFilterEntries: Signal<Array<ActiveFilterChipEntry>> = computed(
    (): Array<ActiveFilterChipEntry> =>
      buildActiveFilterChipEntries(
        this.columnFilters(),
        this.columnHeaderMap(),
        (operator: string): string => this.operatorLabel(operator),
        this.transloco.translate('components.dataGrid.filterMenu.boolTrue'),
        this.transloco.translate('components.dataGrid.filterMenu.boolFalse')
      )
  );

  /** Whether any column filter is actively constraining results. */
  protected hasActiveColumnFilters: Signal<boolean> = computed((): boolean => {
    for (const filter of this.columnFilters().values()) {
      if (isFilterActive(filter)) {
        return true;
      }
    }
    return false;
  });

  /** Whether any explicit grouping is active. */
  protected hasGrouping: Signal<boolean> = computed((): boolean => this.groupByFields().length > 0);

  /** Whether the differentiator field appears anywhere in the grouping chain. */
  protected hasDifferentiatorInChain: Signal<boolean> = computed((): boolean => {
    return this.groupByFields().includes(this.discriminatorField());
  });

  /** Nested group tree built by recursively partitioning data. */
  protected nestedGroupedData: Signal<Array<DataGridGroupNode>> = computed((): Array<DataGridGroupNode> => {
    if (!this.hasGrouping()) {
      return new Array<DataGridGroupNode>();
    }
    void this.typeTotalCounts();
    void this.subGroupCounts();
    return partitionNestedGroups(
      this.data(),
      this.groupByFields(),
      0,
      '',
      undefined,
      this.groupBuildContext()
    );
  });

  /** Whether there is anything to render (rows or summary-seeded headers). */
  protected hasRenderableContent: Signal<boolean> = computed((): boolean =>
    this.hasGrouping() ? this.nestedGroupedData().length > 0 : this.data().length > 0
  );

  /** Drop list IDs for all expanded leaf-level thead elements. */
  protected theadDropListIds: Signal<Array<string>> = computed((): Array<string> => {
    const ids: Array<string> = new Array<string>();
    if (this.hasGrouping()) {
      collectExpandedLeafIds(
        this.nestedGroupedData(),
        ids,
        (group: DataGridGroupNode): boolean => this.isGroupExpanded(group),
        this.groupByFields()
      );
    } else {
      ids.push(DATA_GRID_FLAT_THEAD_ID);
    }
    return ids;
  });

  /** Columns for the flat (ungrouped) table. */
  protected flatColumns: Signal<Array<DataGridColumn>> = computed((): Array<DataGridColumn> => {
    const ordered: Array<DataGridColumn> = orderColumns(this.sharedColumns(), this.resolvedColumnOrder());
    return hideColumns(ordered, this.resolvedHiddenColumns());
  });

  /**
   * Pre-computed leaf columns keyed by element type.
   * When the differentiator is in the group chain, applies per-type grouped header overrides.
   */
  protected leafColumnsMap: Signal<Record<string, Array<DataGridColumn>>> = computed(
    (): Record<string, Array<DataGridColumn>> => {
      const orderedShared: Array<DataGridColumn> = orderColumns(
        this.sharedColumns(),
        this.resolvedColumnOrder()
      );

      if (!this.hasDifferentiatorInChain()) {
        return { __shared__: hideColumns(orderedShared, this.resolvedHiddenColumns()) };
      }

      const typeSpecificMap: Record<string, Array<DataGridColumn>> = this.discriminatorColumns();
      const result: Record<string, Array<DataGridColumn>> = {};

      for (const elementType of Object.keys(typeSpecificMap)) {
        const typeSpecific: Array<DataGridColumn> =
          typeSpecificMap[elementType] ?? new Array<DataGridColumn>();
        const typeOrder: Array<string> =
          this.resolvedDiscriminatorColumnOrder()[elementType] ?? new Array<string>();
        const orderedTypeSpecific: Array<DataGridColumn> = orderColumns(typeSpecific, typeOrder);
        const merged: Array<DataGridColumn> = hideColumns(
          [...orderedShared, ...orderedTypeSpecific],
          this.resolvedHiddenColumns()
        );

        result[elementType] = applyGroupedColumnOverrides(merged, elementType, this.groupedColumnOverrides());
      }

      result['__shared__'] = hideColumns(orderedShared, this.resolvedHiddenColumns());
      return result;
    }
  );

  /** Number of visible shared columns. */
  protected visibleSharedCount: Signal<number> = computed((): number => this.flatColumns().length);

  /** Column sections for the Columns popover — shared plus optional type groups. */
  protected columnSections: Signal<Array<DataGridColumnSection>> = computed(
    (): Array<DataGridColumnSection> =>
      buildColumnSections({
        sharedColumns: this.sharedColumns(),
        columnOrder: this.resolvedColumnOrder(),
        discriminatorColumns: this.discriminatorColumns(),
        discriminatorColumnOrder: this.resolvedDiscriminatorColumnOrder(),
        discriminatorLabels: this.discriminatorLabels(),
        includeDiscriminatorSections: this.resolvedFeatures().discriminatorColumns
      })
  );

  /** Merged list of all column definitions. */
  private allColumns: Signal<Array<DataGridColumn>> = computed((): Array<DataGridColumn> => [
    ...this.sharedColumns(),
    ...Object.values(this.discriminatorColumns()).flat()
  ]);

  /** Pre-computed map of field → header display name. */
  protected columnHeaderMap: Signal<Map<string, string>> = computed((): Map<string, string> => {
    const map: Map<string, string> = new Map<string, string>();
    for (const col of this.allColumns()) {
      map.set(col.field, col.header);
    }
    return map;
  });

  /** Pre-computed map of field → column definition. */
  protected fieldToColumnMap: Signal<Map<string, DataGridColumn>> = computed(
    (): Map<string, DataGridColumn> => {
      const map: Map<string, DataGridColumn> = new Map<string, DataGridColumn>();
      for (const col of this.allColumns()) {
        map.set(col.field, col);
      }
      return map;
    }
  );

  /** Shared inputs forwarded through recursive group-tree instances. */
  protected groupTreeViewModel: Signal<DataGridGroupTreeViewModel> = computed(
    (): DataGridGroupTreeViewModel => ({
      collapsedGroups: this.collapsedGroups(),
      expandedGroups: this.expandedGroups(),
      highCardinalityFields: this.highCardinalityFields(),
      discriminatorField: this.discriminatorField(),
      discriminatorColors: this.discriminatorColors(),
      discriminatorLabels: this.discriminatorLabels(),
      columnHeaders: this.columnHeaderMap(),
      fieldToColumnMap: this.fieldToColumnMap(),
      typeTotalCounts: this.typeTotalCounts(),
      subGroupCounts: this.subGroupCounts(),
      primaryGroupField: this.primaryGroupField(),
      groupBucketStates: this.groupBucketStates(),
      hasActiveColumnFilters: this.hasActiveColumnFilters(),
      visibleSharedCount: this.visibleSharedCount(),
      leafColumnsMap: this.leafColumnsMap(),
      hasDifferentiatorInChain: this.hasDifferentiatorInChain(),
      actionColumnWidth: this.actionColumnWidth(),
      selectionColumnWidth: this.selectionColumnWidth(),
      rowSelectionEnabled: this.selectionColumnWidth() > 0,
      rowActionTemplate: this.rowActionTemplate(),
      showDefaultRowAction: this.showDefaultRowAction(),
      rowHeight: this.rowHeight,
      hoveredRowKey: this.hoveredRowKey(),
      columnFilters: this.columnFilters(),
      groupedFields: this.groupByFields(),
      groupableFields: this.groupableFields(),
      groupingEnabled: this.resolvedFeatures().grouping,
      columnFiltersEnabled: this.resolvedFeatures().columnFilters,
      hasMore: this.hasMore(),
      loading: this.loading(),
      loadingMore: this.loadingMore(),
      columnWidths: this.resolvedColumnWidths(),
      sortStates: this.sortStates(),
      flatSortState: this.flatSortState()
    })
  );

  /** Fixed-length array driving the skeleton loader column @for loop. */
  protected readonly skeletonColumns: ReadonlyArray<number> = Array.from(
    { length: SKELETON_COLUMN_COUNT },
    (_unused: undefined, columnIndex: number): number => columnIndex
  );

  /** Fixed-length array driving the skeleton loader row @for loop. */
  protected readonly skeletonRows: ReadonlyArray<number> = Array.from(
    { length: SKELETON_ROW_COUNT },
    (_unused: undefined, rowIndex: number): number => rowIndex
  );

  /** Fixed row height exposed for child tables. */
  protected readonly rowHeight: number = DATA_GRID_ROW_HEIGHT;

  /** Flat sort-path sentinel for the ungrouped table. */
  protected readonly flatSortPath: string = DATA_GRID_FLAT_SORT_PATH;

  /** Thead id for the ungrouped table. */
  protected readonly flatTheadId: string = DATA_GRID_FLAT_THEAD_ID;

  /** Whether the initial group-by fields have been applied. */
  private initialGroupApplied: boolean = false;

  /** Whether a missing-row-key warning has already been logged this instance. */
  private hasWarnedMissingRowKey: boolean = false;

  /** Last query identity used to decide when to clear selection. */
  private lastSelectionQueryIdentity: string | null = null;

  /** Loaded rows from the previous selection-reconcile pass. */
  private lastLoadedSelectionRows: ReadonlyArray<GridRow> = new Array<GridRow>();

  /** Last descriptor emitted on `selectionChange`, used to skip duplicate ticks. */
  private lastEmittedSelection: GridSelectionDescriptor | null = null;

  /** Transloco key for a grouping-cap warning message. */
  protected groupingCapMessageKey: (warning: GroupingCapWarning) => string = groupingCapMessageKey;

  /**
   * Initializes DeightonDataGridComponent and registers grouping-cap dismiss reset,
   * initial group-by seeding, layout rehydrate, persistence, load-more, selection, and hidden-column defaults.
   */
  constructor() {
    this.registerGroupingCapDismissReset();

    effect((): void => {
      const initial: Array<string> = this.initialGroupByFields();
      if (initial.length > 0 && this.groupByFields().length === 0 && !this.initialGroupApplied) {
        this.initialGroupApplied = true;
        this.groupByFields.set([...initial]);
      }
    });

    this.registerLayoutPersistence();
    this.registerGridStatePersistence();
    this.registerExpandedUnloadedLeafLoads();
    this.registerSelectionEffects();
    this.registerHiddenColumnDefaults();
  }

  /**
   * CDK enter predicate for the group bar drop list.
   *
   * @param drag - Dragged column.
   * @returns True when the column may enter the group bar.
   */
  protected canEnterGroupBar = (drag: CdkDrag<DataGridColumnDragData>): boolean => {
    return drag.data.isShared && this.isGroupableField(drag.data.field);
  };

  /**
   * Removes a filter by field name and emits the change.
   *
   * @param field - The column field name to clear.
   */
  protected removeFilter(field: string): void {
    this.filterChange.emit({ field, filter: null });
  }

  /**
   * Applies a Columns-popover visibility change and notifies the host.
   *
   * @param hidden - Next hidden field set.
   */
  protected onHiddenColumnsChange(hidden: Set<string>): void {
    const next: Set<string> = new Set<string>(hidden);
    this.resolvedHiddenColumns.set(next);
    this.hiddenColumnsChange.emit(new Set<string>(next));
    saveGridLayout(this.persistence, { hiddenColumns: [...next] });
  }

  /**
   * Applies a committed column resize, persists it, and notifies the host.
   *
   * @param event - Field and next width in pixels.
   */
  protected onColumnWidthCommit(event: { field: string; width: number }): void {
    const next: Record<string, number> = {
      ...this.resolvedColumnWidths(),
      [event.field]: event.width
    };
    this.resolvedColumnWidths.set(next);
    saveGridLayout(this.persistence, { columnWidths: next });
    this.columnWidthChange.emit(event);
  }

  /**
   * Opens the filter menu for a column.
   *
   * @param event - The click event.
   * @param col - The column definition.
   */
  protected openFilterMenu(event: MouseEvent, col: DataGridColumn): void {
    event.stopPropagation();
    const anchor: HTMLElement = (event.target as HTMLElement).closest('th') as HTMLElement;
    this.filterMenuAnchor.set(anchor);
    this.openFilterColumn.set(col);
  }

  /** Closes the filter menu. */
  protected closeFilterMenu(): void {
    this.openFilterColumn.set(null);
    this.filterMenuAnchor.set(null);
  }

  /**
   * Handles filter apply from the filter menu popup.
   *
   * @param event - The field and filter to apply.
   */
  protected onFilterMenuApply(event: { field: string; filter: ColumnFilter | null }): void {
    this.filterChange.emit(event);
  }

  /**
   * Returns the active filter for a column field, or null if none.
   *
   * @param field - The column field name.
   * @returns The active filter or null.
   */
  protected getFilterForColumn(field: string): ColumnFilter | null {
    return this.columnFilters().get(field) ?? null;
  }

  /**
   * Opens the filter menu from a child table header button.
   *
   * @param event - Filter-menu open payload from the table.
   */
  protected onChildFilterMenuOpen(event: DataGridFilterMenuOpenEvent): void {
    this.openFilterMenu(event.mouseEvent, event.column);
  }

  /**
   * Cycles sort from a child table header click.
   *
   * @param event - Header sort payload.
   */
  protected onChildHeaderSort(event: DataGridHeaderSortEvent): void {
    if (event.sortPath === DATA_GRID_FLAT_SORT_PATH) {
      this.sortFlat(event.field);
      return;
    }
    this.sortGroup(event.sortPath, event.field);
  }

  /**
   * Forwards a child table column reorder.
   *
   * @param event - Reorder payload.
   */
  protected onChildColumnReorder(event: DataGridColumnReorderEvent): void {
    this.onColumnReorder(event.event, event.sortPath, event.columns);
  }

  /**
   * Returns true if the given group is expanded.
   *
   * @param group - Group node.
   * @returns Whether the body should render.
   */
  protected isGroupExpanded(group: DataGridGroupNode): boolean {
    return groupIsExpanded(
      group,
      this.collapsedGroups(),
      this.expandedGroups(),
      this.highCardinalityFields()
    );
  }

  /**
   * Toggles expanded/collapsed state for a group.
   *
   * @param group - Group node.
   */
  protected toggleGroup(group: DataGridGroupNode): void {
    const expanding: boolean = !this.isGroupExpanded(group);

    this.collapsedGroups.update((current: Set<string>): Set<string> => {
      const next: Set<string> = new Set<string>(current);
      if (expanding) {
        next.delete(group.path);
      } else {
        next.add(group.path);
      }
      return next;
    });
    this.expandedGroups.update((current: Set<string>): Set<string> => {
      const next: Set<string> = new Set<string>(current);
      if (expanding) {
        next.add(group.path);
      } else {
        next.delete(group.path);
      }
      return next;
    });

    if (expanding) {
      const paths: Array<string> = new Array<string>();
      collectLoadsForExpandedLeaves(
        group,
        paths,
        (node: DataGridGroupNode): boolean => this.isGroupExpanded(node),
        this.groupByFields()
      );
      for (const path of paths) {
        this.loadMore.emit(path);
      }
    }
  }

  /**
   * Recursively collects expanded leaf paths with no loaded rows.
   *
   * @param groups - Group nodes to traverse.
   * @param acc - Accumulator.
   */
  private collectExpandedUnloadedLeaves(groups: Array<DataGridGroupNode>, acc: Array<string>): void {
    collectUnloadedLeaves(
      groups,
      acc,
      (group: DataGridGroupNode): boolean => this.isGroupExpanded(group),
      this.groupByFields()
    );
  }

  /**
   * Cycles sort for a column within a group.
   *
   * @param path - Composite path.
   * @param field - Column field.
   */
  protected sortGroup(path: string, field: string): void {
    const next: DataGridGroupSortState | null = cycleSortState(this.sortStates()[path] ?? null, field);
    this.sortStates.update(
      (
        current: Record<string, DataGridGroupSortState | null>
      ): Record<string, DataGridGroupSortState | null> => {
        return { ...current, [path]: next };
      }
    );
    this.sortChange.emit(next !== null ? { field: next.field, direction: next.dir } : null);
  }

  /**
   * Returns true if the given field is currently in the active group chain.
   *
   * @param field - Column field.
   * @returns True when grouped.
   */
  protected isGroupedByField(field: string): boolean {
    return this.groupByFields().includes(field);
  }

  /**
   * Returns true if the differentiator field is in the active group chain.
   *
   * @returns True when grouped by the discriminator.
   */
  protected isDiscriminatorGrouped(): boolean {
    return this.isGroupedByField(this.discriminatorField());
  }

  /** Dismisses the info banner and emits so the parent can persist the change. */
  protected dismissInfoBanner(): void {
    this.infoBannerDismissedChange.emit(true);
  }

  /**
   * Dismisses the grouping-cap warning banner for the given cap kind this session.
   *
   * @param kind - Cap kind to dismiss.
   */
  protected dismissGroupingCapBanner(kind: GroupingCapKind): void {
    this.dismissedGroupingCapKinds.update((current: Set<GroupingCapKind>): Set<GroupingCapKind> => {
      const next: Set<GroupingCapKind> = new Set<GroupingCapKind>(current);
      next.add(kind);
      return next;
    });
  }

  /**
   * Returns true if the given field is allowed to be used for grouping.
   *
   * @param field - Column field.
   * @returns True when groupable.
   */
  protected isGroupableField(field: string): boolean {
    const allowed: Array<string> = this.groupableFields();
    return allowed.length === 0 || allowed.includes(field);
  }

  /**
   * Toggles a field in the group chain.
   *
   * @param field - Column field.
   */
  protected toggleGroupField(field: string): void {
    const previousPrimary: string | undefined = this.groupByFields()[0];
    const current: Array<string> = this.groupByFields();
    let next: Array<string>;

    if (current.includes(field)) {
      next = current.filter((existing: string): boolean => existing !== field);
    } else if (!this.isGroupableField(field)) {
      return;
    } else {
      next = [...current, field];
    }

    this.groupByFields.set(next);
    if (next[0] !== previousPrimary) {
      this.resetGroupingInteractionState();
    }
  }

  /**
   * Removes a specific field from the group chain.
   *
   * @param field - Column field.
   */
  protected removeGroupField(field: string): void {
    const previousPrimary: string | undefined = this.groupByFields()[0];
    const next: Array<string> = this.groupByFields().filter(
      (existing: string): boolean => existing !== field
    );
    this.groupByFields.set(next);
    if (next[0] !== previousPrimary) {
      this.resetGroupingInteractionState();
    }
  }

  /** Clears all grouping — reverts to flat ungrouped view. */
  protected clearAllGroups(): void {
    this.groupByFields.set(new Array<string>());
    this.collapsedGroups.set(new Set<string>());
    this.expandedGroups.set(new Set<string>());
    this.sortStates.set({});
    this.flatSortState.set(null);
  }

  /**
   * Looks up the display header for a field.
   *
   * @param field - Column field.
   * @returns Header, or the raw field.
   */
  protected getColumnHeaderForField(field: string): string {
    return this.columnHeaderMap().get(field) ?? field;
  }

  /**
   * Handles a drop event on the group bar.
   *
   * @param event - CDK drop event.
   */
  protected onColumnDroppedOnGroupBar(
    event: CdkDragDrop<Array<string>, Array<string>, DataGridColumnDragData>
  ): void {
    if (event.previousContainer === event.container) {
      const fields: Array<string> = [...this.groupByFields()];
      const previousPrimary: string | undefined = fields[0];
      moveItemInArray(fields, event.previousIndex, event.currentIndex);
      this.groupByFields.set(fields);
      if (fields[0] !== previousPrimary) {
        this.resetGroupingInteractionState();
      }
    } else {
      const dragData: DataGridColumnDragData = event.item.data;
      if (this.isGroupedByField(dragData.field) || !this.isGroupableField(dragData.field)) {
        return;
      }
      const fields: Array<string> = [...this.groupByFields()];
      fields.splice(event.currentIndex, 0, dragData.field);
      this.groupByFields.set(fields);
    }
  }

  /**
   * Handles a CDK drop event within a thead drop list for column reordering.
   *
   * @param event - CDK drop event.
   * @param sortPath - Group path.
   * @param columns - Visible columns.
   */
  protected onColumnReorder(
    event: CdkDragDrop<Array<string>, Array<string>, DataGridColumnDragData>,
    sortPath: string,
    columns: Array<DataGridColumn>
  ): void {
    if (event.previousContainer !== event.container) {
      return;
    }

    const prevIdx: number = event.previousIndex;
    const currIdx: number = event.currentIndex;

    if (prevIdx === currIdx) {
      return;
    }

    const sharedCount: number = this.visibleSharedCount();
    const bothShared: boolean = prevIdx < sharedCount && currIdx < sharedCount;
    const bothTypeSpecific: boolean = prevIdx >= sharedCount && currIdx >= sharedCount;

    if (!bothShared && !bothTypeSpecific) {
      return;
    }

    if (bothShared) {
      const reordered: Array<DataGridColumn> = [...columns.slice(0, sharedCount)];
      moveItemInArray(reordered, prevIdx, currIdx);
      const order: Array<string> = reordered.map((col: DataGridColumn): string => col.field);
      this.resolvedColumnOrder.set(order);
      saveGridLayout(this.persistence, { columnOrder: order });
      this.columnOrderChange.emit(order);
    } else {
      const typeSpecificSlice: Array<DataGridColumn> = [...columns.slice(sharedCount)];
      const localPrev: number = prevIdx - sharedCount;
      const localCurr: number = currIdx - sharedCount;
      moveItemInArray(typeSpecificSlice, localPrev, localCurr);
      const order: Array<string> = typeSpecificSlice.map((col: DataGridColumn): string => col.field);
      const elementType: string = resolveLeafElementType(
        sortPath === DATA_GRID_FLAT_SORT_PATH
          ? this.data()
          : resolveRowsForPath(sortPath, this.nestedGroupedData()),
        this.discriminatorField(),
        this.discriminatorLabels()
      );
      const nextTypeOrder: Record<string, Array<string>> = {
        ...this.resolvedDiscriminatorColumnOrder(),
        [elementType]: order
      };
      this.resolvedDiscriminatorColumnOrder.set(nextTypeOrder);
      saveGridLayout(this.persistence, { discriminatorColumnOrder: nextTypeOrder });
      this.discriminatorColumnOrderChange.emit({ elementType, order });
    }
  }

  /**
   * Cycles sort for a column in the flat view.
   *
   * @param field - Column field.
   */
  protected sortFlat(field: string): void {
    const next: DataGridGroupSortState | null = cycleSortState(this.flatSortState(), field);
    this.flatSortState.set(next);
    this.sortChange.emit(next !== null ? { field: next.field, direction: next.dir } : null);
  }

  /**
   * Returns sorted data for the flat view.
   *
   * @returns Sorted or original rows.
   */
  protected getSortedFlatData(): Array<Record<string, unknown>> {
    return rowsForSortState(
      this.data(),
      this.flatSortState(),
      this.hasMore() || this.totalCount() > this.data().length
    );
  }

  /**
   * Clears expand/collapse and per-group sort state after the primary group field changes.
   */
  private resetGroupingInteractionState(): void {
    this.collapsedGroups.set(new Set<string>());
    this.expandedGroups.set(new Set<string>());
    this.sortStates.set({});
    this.flatSortState.set(null);
  }

  /**
   * Server summary maps used to seed and count nested groups.
   *
   * @returns Build context.
   */
  private groupBuildContext(): DataGridGroupBuildContext {
    return {
      fieldToColumnMap: this.fieldToColumnMap(),
      typeTotalCounts: this.typeTotalCounts(),
      subGroupCounts: this.subGroupCounts(),
      primaryGroupField: this.primaryGroupField(),
      discriminatorField: this.discriminatorField(),
      discriminatorLabels: this.discriminatorLabels()
    };
  }

  /**
   * Returns a translated label for a filter operator.
   *
   * @param operator - Operator string.
   * @returns Display label.
   */
  private operatorLabel(operator: string): string {
    const key: string = `components.dataGrid.operators.${operator}`;
    const translated: string = this.transloco.translate(key);
    return translated !== key ? translated : operator;
  }

  /**
   * Whether a declarative action is enabled for the current selection.
   *
   * @param action - Action definition.
   * @returns True when the button should be enabled.
   */
  protected isActionEnabled(action: GridAction): boolean {
    return isGridActionEnabled(action, this.selectionStore.selectionType(), this.selectionDescriptor());
  }

  /**
   * Clears the current selection.
   */
  protected clearSelection(): void {
    this.selectionStore.clear();
  }

  /**
   * Selects every row matching the live query, including unfetched pages.
   */
  protected selectAllMatchingRows(): void {
    this.selectionStore.selectAllMatching();
  }

  /**
   * Toggles the grouped select-all checkbox between all-matching and clear.
   */
  protected toggleGroupedSelectAll(): void {
    this.selectionStore.toggleSelectAllMatching();
  }

  /**
   * Emits a consumer action with the live selection descriptor.
   *
   * @param action - Triggered action.
   */
  protected onGridAction(action: GridAction): void {
    this.gridActionTriggered.emit({
      actionId: action.id,
      selection: this.selectionDescriptor()
    });
  }

  /**
   * Handles Ctrl/Cmd+A and Escape on the grid host.
   *
   * @param event - Keyboard event.
   */
  protected onHostKeydown(event: KeyboardEvent): void {
    if (this.selectionColumnWidth() === 0) {
      return;
    }
    if (event.key === 'Escape') {
      if (this.openFilterColumn() !== null) {
        return;
      }
      if (this.selectionCount() > 0) {
        event.preventDefault();
        this.selectionStore.clear();
      }
      return;
    }
    const isSelectAll: boolean = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a';
    if (!isSelectAll) {
      return;
    }
    const target: HTMLElement = event.target as HTMLElement;
    if (target.closest('input, textarea') !== null) {
      return;
    }
    event.preventDefault();
    this.selectionStore.selectAllLoaded(this.data());
  }

  /**
   * Seeds hidden columns from definition `hidden` flags when no persisted or host set exists.
   * Runs once when persistence is ready and column definitions have loaded.
   */
  private registerHiddenColumnDefaults(): void {
    const columnsAvailable: Signal<boolean> = computed((): boolean => {
      if (!this.persistence.ready()) {
        return false;
      }
      return this.sharedColumns().length > 0 || Object.keys(this.discriminatorColumns()).length > 0;
    });

    toObservable(columnsAvailable)
      .pipe(
        filter((isAvailable: boolean): boolean => isAvailable),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((): void => {
        this.seedHiddenColumnDefaults();
      });
  }

  /**
   * Applies definition `hidden` flags when neither persistence nor the host provided a hidden set.
   */
  private seedHiddenColumnDefaults(): void {
    if (this.persistence.state().hiddenColumns.length > 0 || this.resolvedHiddenColumns().size > 0) {
      return;
    }
    const defaultHidden: Set<string> = collectHiddenByDefaultFields(
      this.sharedColumns(),
      this.discriminatorColumns()
    );
    if (defaultHidden.size === 0) {
      return;
    }
    this.resolvedHiddenColumns.set(defaultHidden);
    this.hiddenColumnsChange.emit(defaultHidden);
    saveGridLayout(this.persistence, { hiddenColumns: [...defaultHidden] });
  }

  /**
   * Rehydrates layout from persistence once the port reports ready.
   */
  private registerLayoutPersistence(): void {
    toObservable(this.persistence.ready)
      .pipe(
        filter((isReady: boolean): boolean => isReady),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((): void => {
        this.applyPersistedLayout();
      });
  }

  /**
   * Copies persisted layout fields onto the resolved grid signals.
   */
  private applyPersistedLayout(): void {
    const state: DataGridState = this.persistence.state();
    if (state.hiddenColumns.length > 0) {
      this.resolvedHiddenColumns.set(new Set<string>(state.hiddenColumns));
    }
    if (state.columnOrder.length > 0) {
      this.resolvedColumnOrder.set([...state.columnOrder]);
    }
    if (Object.keys(state.discriminatorColumnOrder).length > 0) {
      this.resolvedDiscriminatorColumnOrder.set({ ...state.discriminatorColumnOrder });
    }
    if (Object.keys(state.columnWidths).length > 0) {
      this.resolvedColumnWidths.set({ ...state.columnWidths });
    }
    if (state.groupByFields.length > 0 && this.groupByFields().length === 0) {
      const allowed: Array<string> = this.groupableFields();
      const restored: Array<string> =
        allowed.length === 0
          ? [...state.groupByFields]
          : state.groupByFields.filter((field: string): boolean => allowed.includes(field));
      if (restored.length > 0) {
        this.initialGroupApplied = true;
        this.groupByFields.set(restored);
      }
    }
  }

  /**
   * Persists group-by changes and notifies the host via `gridStateChange`.
   *
   * The effect depends only on `groupByFields()`. Layout signals are read inside
   * `untracked()` so `saveGridLayout` / host `hiddenColumns` updates do not re-enter
   * the effect and trigger infinite change detection. Emission is deferred with
   * `queueMicrotask` so the host sees layout state after the current CD turn.
   */
  private registerGridStatePersistence(): void {
    let stateEffectInitialized: boolean = false;
    effect((): void => {
      const fields: Array<string> = this.groupByFields();
      if (!stateEffectInitialized) {
        stateEffectInitialized = true;
        return;
      }
      saveGridLayout(this.persistence, { groupByFields: fields });
      queueMicrotask((): void => {
        const payload: DataGridState = untracked((): DataGridState => ({
          groupByFields: fields,
          hiddenColumns: [...this.resolvedHiddenColumns()],
          columnOrder: this.resolvedColumnOrder(),
          discriminatorColumnOrder: this.resolvedDiscriminatorColumnOrder(),
          columnWidths: this.resolvedColumnWidths()
        }));
        this.gridStateChange.emit(payload);
      });
    });
  }

  /**
   * Clears session dismiss flags when the grouping-cap warning set meaningfully changes.
   *
   * Compares a stable fingerprint so a new warnings array reference with the same
   * content does not reset dismiss state on every orchestration refresh.
   */
  private registerGroupingCapDismissReset(): void {
    effect((): void => {
      const warnings: Array<GroupingCapWarning> = this.groupingCapWarnings();
      const fingerprint: string = warnings
        .map(
          (warning: GroupingCapWarning): string =>
            `${warning.kind}:${warning.skippedCount}:${warning.cap ?? ''}`
        )
        .join('|');
      if (fingerprint === this.lastGroupingCapWarningsFingerprint) {
        return;
      }
      this.lastGroupingCapWarningsFingerprint = fingerprint;
      this.dismissedGroupingCapKinds.set(new Set<GroupingCapKind>());
    });
  }

  /**
   * Requests leaf pages for expanded groups that have no loaded rows.
   */
  private registerExpandedUnloadedLeafLoads(): void {
    effect((): void => {
      const groups: Array<DataGridGroupNode> = this.nestedGroupedData();
      const paths: Array<string> = new Array<string>();
      this.collectExpandedUnloadedLeaves(groups, paths);
      if (paths.length === 0) {
        return;
      }
      queueMicrotask((): void => {
        for (const path of paths) {
          this.loadMore.emit(path);
        }
      });
    });
  }

  /**
   * Wires the selection store to grid inputs, emits `selectionChange` when the
   * descriptor meaningfully changes, clears selection when the query identity
   * changes, and drops stale keys after a same-query data refresh.
   */
  private registerSelectionEffects(): void {
    effect((): void => {
      const resolvable: boolean = this.rowKeyResolvable();
      const selectionOn: boolean =
        this.resolvedFeatures().rowSelection && this.selectionUiEnabled() && resolvable;
      this.selectionStore.setMode(selectionOn ? this.rowSelectionMode() : 'none');
      const rowKeyFn: GridRowKeyFn | null = this.rowKeyFn();
      const rowKeyField: string = this.rowKeyField();
      this.selectionStore.setRowKeyResolver(
        (row: GridRow): string => resolveRowKey(row, rowKeyFn, rowKeyField) ?? ''
      );
      this.selectionStore.setQueryContext({
        groupByFields: this.groupByFields(),
        columnFilters: serializeFilters(this.columnFilters()),
        totalCount: this.totalCount()
      });
      if (
        this.resolvedFeatures().rowSelection &&
        this.selectionUiEnabled() &&
        !resolvable &&
        this.data().length > 0 &&
        isDevMode() &&
        !this.hasWarnedMissingRowKey
      ) {
        this.hasWarnedMissingRowKey = true;
        console.warn(
          'd-data-grid: rowSelection is enabled but rows have no resolvable identity. ' +
            'Set rowKeyField or rowKeyFn. Selection is disabled.'
        );
      }
    });

    effect((): void => {
      const descriptor: GridSelectionDescriptor = this.selectionStore.descriptor();
      if (this.lastEmittedSelection === null) {
        this.lastEmittedSelection = descriptor;
        if (descriptor.estimatedCount === 0 && descriptor.includedKeys.length === 0) {
          return;
        }
      } else if (areSelectionDescriptorsEqual(this.lastEmittedSelection, descriptor)) {
        return;
      }
      this.lastEmittedSelection = descriptor;
      this.selectionChange.emit(descriptor);
    });

    effect((): void => {
      this.applyLoadedRowReconciliation(this.selectionQueryIdentity(), this.data());
    });
  }

  /**
   * Reconciles selection against the latest loaded rows for an unchanged query.
   *
   * A refresh that clears `data()` while `loading` is true must not replace the
   * previous window, or the following page cannot detect deleted keys. A settled
   * empty query (`totalCount === 0` and not loading) drops that window.
   *
   * @param identity - Current selection query identity.
   * @param rows - Latest `data()` rows.
   */
  private applyLoadedRowReconciliation(identity: string, rows: ReadonlyArray<GridRow>): void {
    if (this.lastSelectionQueryIdentity === null) {
      this.lastSelectionQueryIdentity = identity;
      this.lastLoadedSelectionRows = rows;
      return;
    }
    if (identity !== this.lastSelectionQueryIdentity) {
      this.lastSelectionQueryIdentity = identity;
      this.lastLoadedSelectionRows = rows;
      this.selectionStore.clear();
      return;
    }

    const loading: boolean = this.loading();
    const totalCount: number = this.totalCount();
    if (rows.length === 0) {
      if (!loading && totalCount === 0 && this.lastLoadedSelectionRows.length > 0) {
        this.selectionStore.reconcileLoadedRows(this.lastLoadedSelectionRows, rows);
        this.lastLoadedSelectionRows = rows;
      }
      return;
    }

    this.selectionStore.reconcileLoadedRows(this.lastLoadedSelectionRows, rows);
    this.lastLoadedSelectionRows = rows;
  }

  /**
   * Query identity that should wipe selection: grouping, column filters, and map scope.
   *
   * @returns Stable identity string for the live query.
   */
  private selectionQueryIdentity(): string {
    return [
      this.groupByFields().join('|'),
      JSON.stringify(serializeFilters(this.columnFilters())),
      this.scopeFilterMode() ?? ''
    ].join('::');
  }
}
